import {
  AVAILABILITY_OVERVIEW_SLOT_INTERVAL_MINUTES,
  BackgroundCheckStatus,
  BackgroundCheckStatusLabelMap,
  ClassEligibilityLabelMap,
  classEligibilitySchema,
  classRatioSchema,
  CoachAttendanceGrade,
  CoachAttendanceGradeLabelMap,
  CoachOverallGrade,
  CoachOverallGradeColorMap,
  CoachOverallGradeLabelMap,
  CoachStatus,
  CoachStatusLabelMap,
  CoachStatusColorMap,
  coachStatusSchema,
  CoachStatusTree,
  CoachSubStatus,
  CoachSubStatusLabelMap,
  DayOfWeekLabelMap,
  GetCoachesFilter,
  getAvailabilityOverviewTimeSlotStarts,
  SortedDaysOfWeek,
  GetCoachesSortOrder,
  TechCheckTypeLabelMap,
  TechCheckType,
  EligibleCoachForClassTime,
  FullCoach,
  coachOverallGradeRankOrder,
  TeachingPreference,
  TeachingPreferenceLabelMap,
  LoggedInUser,
} from '@airreading/common/data';
import { createFormOptions } from './form-utils';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { Temporal } from 'temporal-polyfill';
import { formatPlainTime } from '@airreading/common';

export const allCoachStatuses = Object.values(CoachStatus);

export function getCoachStatusLabel(status: CoachStatus | null | undefined): string {
  if (!status) return '-';
  return CoachStatusLabelMap[status] ?? '-';
}

export function getCoachStatusColor(status: CoachStatus | null | undefined): string {
  if (!status) return '';
  return CoachStatusColorMap[status] ?? '';
}

export function getBackgroundCheckStatusLabel(status: BackgroundCheckStatus | null | undefined): string | null {
  if (!status) return null;
  return BackgroundCheckStatusLabelMap[status] ?? null;
}

export const backgroundCheckStatusOptions = Object.values(BackgroundCheckStatus).map((value) => ({
  label: BackgroundCheckStatusLabelMap[value],
  value,
}));

export function getTechCheckTypeLabel(tech: TechCheckType | null | undefined): string | null {
  if (!tech) return null;
  return TechCheckTypeLabelMap[tech] ?? null;
}

export const techCheckStatusOptions = Object.values(TechCheckType).map((value) => ({
  label: TechCheckTypeLabelMap[value],
  value,
}));

export const classEligibilityOptions = classEligibilitySchema.options.map((value) => ({
  label: ClassEligibilityLabelMap[value],
  value,
}));

export const classRatioOptions = classRatioSchema.options.map((value) => ({
  label: value,
  value,
}));

export const availabilityOverviewTimeSlotOptions = getAvailabilityOverviewTimeSlotStarts().map((value) => {
  const start = Temporal.PlainTime.from(value);
  const end = start.add({ minutes: AVAILABILITY_OVERVIEW_SLOT_INTERVAL_MINUTES });
  return {
    label: `${formatPlainTime(start, { hour12: true })} - ${formatPlainTime(end, { hour12: true })}`,
    value,
  };
});

export const teachingDaysOptions = SortedDaysOfWeek.map((value) => ({
  label: DayOfWeekLabelMap[value].full,
  value,
}));

export function getCoachAttendanceGradeLabel(attendanceGrade: CoachAttendanceGrade | null | undefined): string {
  if (!attendanceGrade) return '-';
  return CoachAttendanceGradeLabelMap[attendanceGrade] ?? '-';
}

export function getCoachAttendanceGradeOptions() {
  return Object.values(CoachAttendanceGrade)
    .filter((value) => value !== CoachAttendanceGrade.S)
    .map((value) => ({
      value,
      label: getCoachAttendanceGradeLabel(value),
    }));
}

export function getCoachOverallGradeOptions() {
  return Object.values(CoachOverallGrade).map((value) => ({
    value,
    label: getCoachOverallGradeLabel(value),
  }));
}

export function getCoachOverallGradeLabel(overallGrade: CoachOverallGrade | null | undefined): string {
  if (!overallGrade) return '-';
  return CoachOverallGradeLabelMap[overallGrade] ?? '-';
}

export function getCoachOverallGradeColor(overallGrade: CoachOverallGrade | null | undefined): string {
  if (!overallGrade) return '';
  return CoachOverallGradeColorMap[overallGrade] ?? '';
}

export function getTeachingPreferenceLabel(preference: TeachingPreference | null | undefined): string {
  if (!preference) return '-';
  return TeachingPreferenceLabelMap[preference] ?? '-';
}

export function getTeachingPreferencesLabel(preferences: TeachingPreference[] | null | undefined): string {
  if (!preferences || preferences.length === 0) return '-';
  const hasRegular = preferences.includes(TeachingPreference.regular_schedule);
  const hasSubstitute = preferences.includes(TeachingPreference.substitute_teaching);
  if (hasRegular && hasSubstitute) return 'Both regular and substitute';
  if (hasRegular) return 'Regular schedule only';
  if (hasSubstitute) return 'Substitute teaching only';
  return '-';
}

export const coachStatusOptions = createFormOptions<CoachStatus>(coachStatusSchema.options, CoachStatusLabelMap);

export const coachStatusGroupedOptions = coachStatusSchema.options.map((status) => ({
  label: CoachStatusLabelMap[status],
  value: status,
  children: CoachStatusTree[status].map((subStatus: CoachSubStatus) => ({
    label: CoachSubStatusLabelMap[subStatus],
    value: subStatus,
  })),
}));

export enum AllUpdateTeacherTypeValues {
  Available = 'available',
  All = 'all',
}

export const AllSelectTeacherTypes: MultiSelectOption[] = [
  {
    label: 'Available teachers only',
    value: AllUpdateTeacherTypeValues.Available,
  },
  {
    label: 'All teachers',
    value: AllUpdateTeacherTypeValues.All,
  },
];

export interface CoachesTypeaheadSearchOptions {
  shouldDisplayOverallGrade?: boolean;
  filters?: Omit<GetCoachesFilter, 'query'>;
  sortBy?: GetCoachesSortOrder;
  /** When true, an empty search loads the first page of results instead of returning nothing until the user types. */
  browseOnEmpty?: boolean;
  sortByAvailabilityPriority?: boolean;
}

export const sortByOverallGrade = (coaches: EligibleCoachForClassTime[]) => {
  return [...coaches].sort((a, b) => {
    const aGrade = a.overallGrade;
    const bGrade = b.overallGrade;

    if (aGrade === null && bGrade === null) return 0;
    if (aGrade === null) return 1;
    if (bGrade === null) return -1;

    const aIndex = coachOverallGradeRankOrder.indexOf(aGrade);
    const bIndex = coachOverallGradeRankOrder.indexOf(bGrade);

    return aIndex - bIndex;
  });
};

export const getOverallGradeIndex = (grade: CoachOverallGrade) => {
  if (!grade) return Number.MAX_SAFE_INTEGER;
  return coachOverallGradeRankOrder.indexOf(grade);
};

export const isCoachOffboarded = (loggedInUser: LoggedInUser | null | undefined) => {
  return loggedInUser?.coachStatus === CoachStatus.offboarded;
};

const availableCoachesGradePriority: Record<NonNullable<CoachOverallGrade>, number> = {
  [CoachOverallGrade.S]: 0,
  [CoachOverallGrade.A]: 1,
  [CoachOverallGrade.B]: 2,
  [CoachOverallGrade.C]: 3,
  [CoachOverallGrade.Never_Taught]: 4,
  [CoachOverallGrade.Not_reviewed]: 5,
  [CoachOverallGrade.D]: 6,
};

function getAvailableCoachGradePriority(grade: CoachOverallGrade | null | undefined): number {
  return availableCoachesGradePriority[grade ?? CoachOverallGrade.Not_reviewed];
}

/**
 * Sorts available coaches
 * Primary: fewest active classes first (0 to n)
 * Secondary tiebreaker: availableCoachesGradePriority
 */
export function sortAvailableCoachesByPriority(coaches: FullCoach[]): FullCoach[] {
  return [...coaches].sort((a, b) => {
    const countA = a.studentGroupCount ?? 0;
    const countB = b.studentGroupCount ?? 0;

    if (countA !== countB) {
      return countA - countB;
    }

    const gradeA = getAvailableCoachGradePriority(a.overallGrade);
    const gradeB = getAvailableCoachGradePriority(b.overallGrade);
    return gradeA - gradeB;
  });
}

export const isCoachOnboarding = (loggedInUser: LoggedInUser | null | undefined) => {
  return loggedInUser?.coachStatus === CoachStatus.onboarding;
};
